import { ComponentStateTypes } from "@/components/common/ComponentState";
import Button from "@/components/common/button/Button";
import Page from "@/components/common/page/Page";
import BooleanView from "@/components/common/views/BooleanView";
import { api } from "@/config/axios-context";
import useAppContext from "@/utilities/hooks/useAppContext";
import { useEffect, useState } from "react";
import { Trans } from "react-i18next";

type FormDataNotificationType = {
    notificationType: string;
    group: 'agriculture' | 'accommodation',
    isEmailNotificationEnabled: boolean;
    isNtfyNotificationEnabled: boolean;
    isPushNotificationEnabled?: boolean;
}

type FormDataResponse = {
    email: {
        isEnabled: boolean;
    },
    ntfy: {
        isEnabled: boolean;
        serverUrl: string;
        topic: string;
    },
    push?: {
        isEnabled: boolean;
        publicKey?: string;
    },
    notificationConfigs: FormDataNotificationType[];
}

const NotificationConfigPage = () => {
    const context = useAppContext();
    const [state, setState] = useState<ComponentStateTypes>('');
    const [formData, setFormData] = useState<FormDataResponse | null>(null);
    const [isBrowserPushEnabled, setIsBrowserPushEnabled] = useState(false);
    const [isEnablingPush, setIsEnablingPush] = useState(false);
    const [isDisablingPush, setIsDisablingPush] = useState(false);

    useEffect(() => {
        async function fetchData() {
            setState('loading');
            try {
                const response = await api.get<FormDataResponse>("/NotificationConfig/getFormData");
                if (!response.success) {
                    throw new Error("Failed to fetch form data");
                }

                if (response.data) {
                    setFormData(response.data);
                    if (response.data.push?.isEnabled) {
                        await checkBrowserPushStatus();
                    }
                }

                setState('');
            } catch (error) {
                setState('error');
                console.error(error);
                throw error;
            }
        }

        fetchData();
    }, []);

    async function checkBrowserPushStatus() {
        if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
            setIsBrowserPushEnabled(false);
            return;
        }

        if (Notification.permission !== "granted") {
            setIsBrowserPushEnabled(false);
            return;
        }

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsBrowserPushEnabled(subscription !== null);
    }

    function urlBase64ToUint8Array(value: string) {
        const padding = "=".repeat((4 - value.length % 4) % 4);
        const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        return Uint8Array.from([...rawData].map(character => character.charCodeAt(0)));
    }

    async function enableBrowserPush() {
        const publicKey = formData?.push?.publicKey;
        if (!publicKey || !("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
            context.notify("error", "Push notifications are not supported by this browser");
            return;
        }

        setIsEnablingPush(true);
        try {
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                context.notify("error", "Notification permission was not granted");
                return;
            }

            const registration = await navigator.serviceWorker.ready;
            const existingSubscription = await registration.pushManager.getSubscription();
            const subscription = existingSubscription ?? await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey)
            });
            const subscriptionData = subscription.toJSON();
            if (!subscriptionData.keys?.p256dh || !subscriptionData.keys?.auth) {
                throw new Error("Push subscription keys are missing");
            }

            const response = await api.post("/NotificationConfig/subscribe", {
                endpoint: subscription.endpoint,
                keys: {
                    p256dh: subscriptionData.keys.p256dh,
                    auth: subscriptionData.keys.auth
                }
            });
            if (!response.success) {
                throw new Error("Failed to save push subscription");
            }

            setIsBrowserPushEnabled(true);
        } catch (error) {
            console.error(error);
            context.notify("error", "Failed to enable push notifications");
        } finally {
            setIsEnablingPush(false);
        }
    }

    async function disableBrowserPush() {
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
            context.notify("error", "Push notifications are not supported by this browser");
            return;
        }

        setIsDisablingPush(true);
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (!subscription) {
                setIsBrowserPushEnabled(false);
                return;
            }

            const response = await api.post("/NotificationConfig/unsubscribe", {
                endpoint: subscription.endpoint
            });
            if (!response.success) {
                throw new Error("Failed to remove push subscription");
            }

            const isUnsubscribed = await subscription.unsubscribe();
            if (!isUnsubscribed) {
                throw new Error("Browser failed to unsubscribe from push notifications");
            }

            setIsBrowserPushEnabled(false);
        } catch (error) {
            console.error(error);
            context.notify("error", "Failed to disable push notifications");
            await checkBrowserPushStatus();
        } finally {
            setIsDisablingPush(false);
        }
    }

    async function saveChanges(notificationType: FormDataNotificationType) {
        try {
            const response = await api.post("/NotificationConfig/save", {
                notificationType: notificationType.notificationType,
                isEmailNotificationEnabled: notificationType.isEmailNotificationEnabled,
                isNtfyNotificationEnabled: notificationType.isNtfyNotificationEnabled,
                isPushNotificationEnabled: notificationType.isPushNotificationEnabled
            });
        } catch (error) {
            console.error(error);
            context.notify('error', 'Failed to save changes');
            throw error;
        }
    }

    async function deleteNotificationConfig(notificationType: string) {
        try {
            const response = await api.delete("/NotificationConfig/deleteByName/" + notificationType);
        } catch (error) {
            console.error(error);
            context.notify('error', 'Failed to save changes');
            throw error;
        }
    }

    function handleNotificationTypeChange(notificationType: FormDataNotificationType, newValue: 'Email' | 'Ntfy' | 'None' | 'Push') {
        const config = formData?.notificationConfigs.find(c => c.notificationType === notificationType.notificationType);
        if (!config) return;

        config.isEmailNotificationEnabled = newValue === 'Email';
        config.isNtfyNotificationEnabled = newValue === 'Ntfy';
        config.isPushNotificationEnabled = newValue === 'Push';
        if (newValue === 'None') {
            deleteNotificationConfig(config.notificationType).then();
        }
        else {
            saveChanges(config).then();
        }
        setFormData(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                notificationConfigs: prev.notificationConfigs.map(c => c.notificationType === config.notificationType ? config : c)
            }
        });
    }

    function renderNotificationTypeOptionItem(config: FormDataNotificationType, option: 'None' | 'Email' | 'Ntfy' | 'Push', isSelected: boolean, isDisabled = false) {
        return (
            <button onClick={() => handleNotificationTypeChange(config, option)} 
                className={(isSelected ? 'bg-primary rounded-full hover:text-white' : 'cursor-pointer') + ' px-4 py-2'}
                disabled={isDisabled}
            >
                {option}
            </button>
        );
    }

    function renderNotificationTypeGroup(configs: FormDataNotificationType[], groupNameTranslation: string) {
        return (
            <div className="py-10">
                <h2><Trans i18nKey={groupNameTranslation} /></h2>
                <hr />
                <div className="flex flex-col gap-4 mt-4">
                    {configs.map(config => {
                        return (
                            <div className="bg-neutral-100 dark:bg-neutral-900 hover:shadow dark:hover:shadow-none 
                                    p-4 rounded-2xl 
                                    flex flex-wrap justify-between gap-10
                                    border border-transparent hover:border-neutral-300 dark:hover:border-neutral-500 transition-colors duration-300" 
                                key={config.notificationType}
                            >
                                <div className="flex items-center min-w-[320px]">
                                    <span><Trans i18nKey={`settings.notifications.notificationsConfigTypes.${config.notificationType}`} /></span>
                                </div>
                                <div className="grid grid-cols-4 gap-2 text-center w-max">
                                    {renderNotificationTypeOptionItem(config, 'None', !config.isEmailNotificationEnabled && !config.isNtfyNotificationEnabled && !config.isPushNotificationEnabled)}
                                    {renderNotificationTypeOptionItem(config, 'Email', config.isEmailNotificationEnabled, !formData?.email.isEnabled)}
                                    {renderNotificationTypeOptionItem(config, 'Ntfy', config.isNtfyNotificationEnabled, !formData?.ntfy.isEnabled)}
                                    {renderNotificationTypeOptionItem(config, 'Push', config.isPushNotificationEnabled ?? false, !formData?.push?.isEnabled)}
                                </div>
                            </div>
                        )
                    })}
                </div> 
            </div>
        );
    }

    return (
        <Page title="Notification Config" wFull state={state}>
            <div>
                <h4><Trans i18nKey="settings.notifications.providers" /></h4>
                <div className="flex mt-4">
                    <div className="border-r border-color pr-2">
                        <Trans i18nKey="settings.notifications.providersTypes.email" /> <BooleanView value={formData?.email?.isEnabled} />
                    </div>
                    <div className="border-r border-color px-2">
                        <Trans i18nKey="settings.notifications.providersTypes.ntf" /> <BooleanView value={formData?.ntfy?.isEnabled} />
                    </div>
                    <div className="pl-2">
                        <Trans i18nKey="settings.notifications.providersTypes.push" /> <BooleanView value={formData?.push?.isEnabled} />
                    </div>
                </div>
            </div>
            {formData?.push?.isEnabled && (
                <div className="mt-6">
                    {isBrowserPushEnabled
                        ? <div className="flex items-center gap-4">
                            <p>notification are enabled</p>
                            <Button
                                title="Unsubscribe notifications"
                                disabled={isDisablingPush}
                                onClick={disableBrowserPush}
                            >
                                {isDisablingPush ? "Unsubscribing..." : "Unsubscribe notifications"}
                            </Button>
                        </div>
                        : <Button
                            title="Enable notifications"
                            theme="primary"
                            disabled={isEnablingPush}
                            onClick={enableBrowserPush}
                        >
                            {isEnablingPush ? "Enabling..." : "Enable notifications"}
                        </Button>
                    }
                </div>
            )}
            {renderNotificationTypeGroup(formData?.notificationConfigs.filter(config => config.group === 'agriculture') || [], 'settings.notifications.notificationsConfigTypes.agricultureGroupeNotification')}
            {renderNotificationTypeGroup(formData?.notificationConfigs.filter(config => config.group === 'accommodation') || [], 'settings.notifications.notificationsConfigTypes.accommodationGroupeNotification')}
        </Page>
    );
}

export default NotificationConfigPage;
